package er.prateek.crossly.ui.screens.crossword

import androidx.compose.animation.core.animateFloatAsState
import androidx.compose.animation.core.tween
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.width
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.layout.boundsInRoot
import androidx.compose.ui.layout.onGloballyPositioned
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import er.prateek.crossly.game.crossword.CellPos
import er.prateek.crossly.game.crossword.RackTile
import er.prateek.crossly.ui.components.IconBackspace
import er.prateek.crossly.ui.theme.Spacing
import kotlin.math.floor

/**
 * The trimmed alternative to [KeyboardBar]: a short rack of exactly the letters
 * that are worth trying right now, plus the delete key.
 *
 * Tiles can be tapped to type into the cursor square or dragged straight onto
 * the square they belong in. Tiles the board has already claimed stay in place
 * but go flat — losing a tile mid-word would reflow the rack under the player's
 * thumb, and seeing the ghost of what you've placed is itself a readable bit of
 * progress.
 *
 * Rows are chunked by hand rather than with a flow layout so the tiles keep a
 * single square size and stay centred: a rack of five that stretched edge to
 * edge would read as a keyboard with keys missing.
 */
@Composable
fun LetterRackBar(
    tiles: List<RackTile>,
    onLetter: (Char) -> Unit,
    onDelete: () -> Unit,
    modifier: Modifier = Modifier,
    keyHeight: Dp = 46.dp,
    dragState: LetterDragState? = null,
    onDropOnCell: (CellPos, Char) -> Unit = { _, _ -> },
) {
    val tileWidth = keyHeight * 1.05f
    val deleteWidth = keyHeight * 1.6f

    BoxWithConstraints(
        modifier = modifier
            .fillMaxWidth()
            // Where letters taken off the board head for when no single tile
            // wants them (the whole-alphabet racks reflow as the grid fills).
            .onGloballyPositioned { dragState?.registerRack(it.boundsInRoot()) },
    ) {
        val slot = tileWidth + Spacing.xs
        val perRow = floor(((maxWidth + Spacing.xs) / slot)).toInt().coerceAtLeast(3)
        // The delete key rides along on the last row when there's room for it,
        // and drops to a row of its own when the rack is exactly full.
        val rows = tiles.chunked(perRow)

        Column(
            modifier = Modifier.fillMaxWidth(),
            verticalArrangement = Arrangement.spacedBy(Spacing.xs),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            rows.forEachIndexed { index, rowTiles ->
                val isLastRow = index == rows.lastIndex
                val withDelete = isLastRow && rowTiles.size < perRow
                Row(
                    horizontalArrangement = Arrangement.spacedBy(Spacing.xs),
                    verticalAlignment = Alignment.Top,
                ) {
                    rowTiles.forEach { tile ->
                        LetterTile(
                            tile = tile,
                            width = tileWidth,
                            height = keyHeight,
                            dragState = dragState,
                            onClick = { onLetter(tile.letter) },
                            onDropOnCell = onDropOnCell,
                        )
                    }
                    if (withDelete) {
                        DeleteKey(width = deleteWidth, height = keyHeight, onClick = onDelete)
                    }
                }
            }
            if (rows.isEmpty() || rows.last().size >= perRow) {
                DeleteKey(width = deleteWidth, height = keyHeight, onClick = onDelete)
            }
        }
    }
}

@Composable
private fun LetterTile(
    tile: RackTile,
    width: Dp,
    height: Dp,
    dragState: LetterDragState?,
    onClick: () -> Unit,
    onDropOnCell: (CellPos, Char) -> Unit,
) {
    // Spent tiles settle back a touch, so the live ones read as the taller row.
    val scale by animateFloatAsState(
        targetValue = if (tile.spent) 0.88f else 1f,
        animationSpec = tween(180),
        label = "tileScale",
    )
    GameKey(
        modifier = Modifier
            .width(width)
            .rackTileBounds(tile.letter, dragState, enabled = !tile.spent)
            .draggableLetter(tile.letter, dragState, enabled = !tile.spent, onDropped = onDropOnCell)
            .graphicsLayer {
                scaleX = scale
                scaleY = scale
            },
        height = height,
        enabled = !tile.spent,
        onClick = onClick,
    ) { tint ->
        Text(
            text = tile.letter.toString(),
            fontSize = (height.value * 0.42f).coerceIn(15f, 22f).sp,
            fontWeight = FontWeight.Bold,
            color = tint,
        )
    }
}

@Composable
private fun DeleteKey(width: Dp, height: Dp, onClick: () -> Unit) {
    GameKey(
        modifier = Modifier.width(width),
        height = height,
        emphasized = true,
        onClick = onClick,
    ) { tint ->
        Box(contentAlignment = Alignment.Center) {
            IconBackspace(size = 20.dp, tint = tint)
        }
    }
}
